import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Cpu, RotateCcw, DollarSign, Skull } from 'lucide-react';

const Section = ({ icon, title, color, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
    className="relative rounded-2xl border border-cyan-400/20 bg-slate-950/40 backdrop-blur-md p-6 overflow-hidden"
  >
    <div className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-purple-500/70 via-cyan-400/40 to-transparent" />
    <h2 className={`text-xl font-black uppercase tracking-widest mb-4 flex items-center gap-2 ${color}`}>
      {icon} {title}
    </h2>
    <div className="text-slate-300 text-sm leading-relaxed">{children}</div>
  </motion.section>
);

const InstructionsPage = () => {
  return (
    <div className="relative h-full w-full bg-black overflow-hidden">

      {/* ===== Cyberpunk Lore Background ===== */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]
          bg-[radial-gradient(circle,rgba(139,92,246,0.35),rgba(0,0,0,0.9)_55%)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 240, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0
          bg-[linear-gradient(120deg,transparent,rgba(34,211,238,0.22),transparent)]"
          animate={{ x: ['-120%', '120%'] }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_35%,rgba(0,0,0,0.96)_100%)]" />
      </div>

      <div className="relative z-10 h-full overflow-y-auto px-4 py-10 pb-24">
        <div className="max-w-5xl mx-auto">

          {/* ===== HUD HEADER ===== */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-black tracking-widest uppercase text-cyan-300 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] flex items-center justify-center gap-3">
              <BookOpen /> Learn to Play
            </h1>
            <p className="mt-3 text-cyan-200/70 font-mono">
              Classified Archive: Rules of the SQL Casino Universe
            </p>
          </div>

          <div className="grid gap-8">

            <Section
              icon={<BookOpen />}
              title="How the Game World Works"
              color="text-emerald-300"
            >
              <ul className="space-y-2">
                <li>Spin the data reels to generate valid SQL constructs.</li>
                <li>Correct answers amplify your bets and grant experience.</li>
                <li>Lifelines are tactical hacks: Hint, 50/50, Debug.</li>
                <li>High Roller Cards grant emergency capital every day.</li>
              </ul>
            </Section>

            <Section
              icon={<DollarSign />}
              title="The Casino Economy"
              color="text-yellow-300"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-yellow-400/20 bg-black/40 p-4 rounded-xl">
                  <h3 className="text-yellow-200 font-bold mb-1">Credits ($)</h3>
                  <p className="text-xs text-slate-400">Used for bets and hardware upgrades. Earned via gameplay.</p>
                </div>
                <div className="border border-purple-400/20 bg-black/40 p-4 rounded-xl">
                  <h3 className="text-purple-200 font-bold mb-1">Quantum Gems</h3>
                  <p className="text-xs text-slate-400">Premium currency for elite unlocks and real-world courses.</p>
                </div>
              </div>
            </Section>

            <Section
              icon={<Cpu />}
              title="Rig Augmentation System"
              color="text-cyan-300"
            >
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <li className="border border-cyan-400/20 bg-black/30 p-3 rounded">CPU – Boosts Win Multiplier</li>
                <li className="border border-cyan-400/20 bg-black/30 p-3 rounded">GPU – Increases Jackpot Probability</li>
                <li className="border border-cyan-400/20 bg-black/30 p-3 rounded">RAM – Reduces Lifeline Cost</li>
                <li className="border border-cyan-400/20 bg-black/30 p-3 rounded">PSU – Chance to Refund Failed Bets</li>
              </ul>
            </Section>

            <Section
              icon={<RotateCcw />}
              title="Bankruptcy Protocol"
              color="text-red-300"
            >
              <p>
                When your balance hits zero, the system activates Emergency Recovery Mode.
                You are redirected to High Roller Cards where 4 daily flips grant $1,000 each.
                Use this to rebuild your empire.
              </p>
            </Section>

            <Section
              icon={<Skull />}
              title="Bosses & Anomalies"
              color="text-orange-300"
            >
              <ul className="space-y-2">
                <li><strong className="text-red-400">Boss Nodes:</strong> Appear every 10 levels. Defeat them to unlock new sectors.</li>
                <li><strong className="text-yellow-400">SQL Injection Events:</strong> Security breach puzzles.</li>
                <li><strong className="text-purple-400">System Glitches:</strong> Scrambled queries. Restore order for rare rewards.</li>
              </ul>
            </Section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructionsPage;
